import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Plus,
  Trash2,
  MapPin,
  Library,
  Camera,
  User2,
  Link as LinkIcon,
  Loader2,
  AlertCircle,
  Check,
  ChevronsUpDown,
} from "lucide-react";
import TagPicker from "@/components/TagPicker";
import MediaGallery from "@/components/MediaGallery";
import { resizeImageToDataURL } from "@/lib/imageUtil";
import { searchPlaces, searchPlacesScoped, geocodeAddress } from "@/lib/api";
import {
  COUNTRY_LIST,
  regionLabelForCountry,
  regionOptionsForCountry,
  guessDefaultCountry,
} from "@/lib/geoConstants";
import { toast } from "sonner";

const emptyContact = {
  name: "",
  phone: "",
  email: "",
  notes: "",
  tag_ids: [],
  street: "",
  town: "",
  region: "",
  postcode: "",
  country: "",
  location_name: "",
  lat: null,
  lng: null,
  avatar: "",
  media: [],
  custom_fields: [],
};

export default function ContactDialog({
  open,
  onOpenChange,
  initialContact,
  pin,
  folders,
  tags,
  onSave,
  onDelete,
  onOpenTagLibrary,
}) {
  const [form, setForm] = useState(emptyContact);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState("");
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setNameError("");
      if (initialContact) {
        setForm({
          ...emptyContact,
          ...initialContact,
          tag_ids: initialContact.tag_ids || [],
          media: initialContact.media || [],
          custom_fields: initialContact.custom_fields || [],
          lat: initialContact.lat ?? null,
          lng: initialContact.lng ?? null,
        });
      } else {
        setForm({
          ...emptyContact,
          street: pin?.street || "",
          town: pin?.town || "",
          region: pin?.region || "",
          postcode: pin?.postcode || "",
          country: pin?.country || guessDefaultCountry(),
          location_name: pin?.location_name || "",
          lat: pin?.lat ?? null,
          lng: pin?.lng ?? null,
        });
      }
    }
  }, [open, initialContact, pin]);

  const isEdit = !!initialContact;
  const lat = form.lat;
  const lng = form.lng;
  const locName = isEdit ? initialContact.location_name : pin?.location_name;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  // When the country changes, clear region if the previous value doesn't
  // belong to the new country's list (e.g. Ontario is not a US state).
  const setCountry = (v) => {
    setForm((p) => {
      const nextOptions = regionOptionsForCountry(v);
      const keep = !nextOptions || nextOptions.includes((p.region || "").trim());
      return { ...p, country: v, region: keep ? p.region : "" };
    });
  };

  const addCustomField = () =>
    setForm((p) => ({ ...p, custom_fields: [...p.custom_fields, { key: "", value: "" }] }));
  const updateCustomField = (idx, k, v) =>
    setForm((p) => ({
      ...p,
      custom_fields: p.custom_fields.map((cf, i) => (i === idx ? { ...cf, [k]: v } : cf)),
    }));
  const removeCustomField = (idx) =>
    setForm((p) => ({ ...p, custom_fields: p.custom_fields.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    // 1) Validate name inline (never save silently)
    const name = form.name.trim();
    if (!name) {
      setNameError("Name is required");
      requestAnimationFrame(() => {
        try {
          nameInputRef.current?.focus();
          nameInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch { /* noop */ }
      });
      toast.error("Name is required");
      return;
    }

    setSaving(true);
    const norm = (v) => (typeof v === "string" ? v.trim() || null : v ?? null);

    // 2) If no lat/lng yet and we have enough address info, try to geocode
    //    on save so the pin drops automatically. A failure here NEVER blocks
    //    the save — we keep the entered address text and warn the user.
    let nextLat = form.lat;
    let nextLng = form.lng;
    let geocodeFailed = false;

    const hasCoords = form.lat != null && form.lng != null;
    const hasAddressSignal = !!(
      (form.street && form.street.trim()) ||
      (form.town && form.town.trim()) ||
      (form.postcode && form.postcode.trim())
    );
    if (!hasCoords && hasAddressSignal) {
      try {
        const g = await geocodeAddress({
          street: form.street,
          town: form.town,
          region: form.region,
          postcode: form.postcode,
          country: form.country,
        });
        if (g && Number.isFinite(g.lat) && Number.isFinite(g.lng)) {
          nextLat = g.lat;
          nextLng = g.lng;
        } else {
          geocodeFailed = true;
        }
      } catch {
        geocodeFailed = true;
      }
    }

    const payload = {
      ...form,
      name,
      phone: norm(form.phone),
      email: norm(form.email),
      notes: norm(form.notes),
      tag_ids: form.tag_ids || [],
      avatar: form.avatar || null,
      media: form.media || [],
      street: norm(form.street),
      town: norm(form.town),
      region: norm(form.region),
      postcode: norm(form.postcode),
      country: norm(form.country),
      location_name: norm(form.location_name),
      lat: nextLat ?? null,
      lng: nextLng ?? null,
      custom_fields: form.custom_fields.filter((cf) => cf.key.trim() || cf.value.trim()),
    };

    try {
      await onSave(payload);
      if (geocodeFailed) {
        toast.warning(
          "Contact saved. We couldn't place a map pin — you can drop or drag one later.",
          { duration: 6000 }
        );
      } else if (!hasCoords && nextLat != null) {
        toast.success("Contact saved and pin placed on the map");
      }
    } catch (err) {
      toast.error(
        (err?.response?.data?.detail) || "Failed to save contact. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // When a place is picked from the in-form search, fill all location fields
  // and replace lat/lng so the contact actually moves. The pick is fully
  // authoritative — empty fields on the picked place clear the previous values
  // so the region (and county) always match the new location.
  const applyPickedPlace = (p) => {
    setForm((prev) => ({
      ...prev,
      street: p.street || "",
      town: p.town || "",
      region: p.region || "",
      postcode: p.postcode || "",
      country: p.country || prev.country || guessDefaultCountry() || "United Kingdom",
      location_name: p.short || prev.location_name || "",
      lat: p.lat,
      lng: p.lng,
    }));
    toast.success(`Pin moved to ${p.short || p.name?.split(",")[0] || "selected place"}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-[#111111] border border-white/10 text-white p-0 max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="contact-dialog"
        onPointerDownOutside={(e) => {
          const target = e.detail?.originalEvent?.target;
          if (target instanceof Element && target.closest("[data-testid='media-viewer']")) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          const target = e.detail?.originalEvent?.target;
          if (target instanceof Element && target.closest("[data-testid='media-viewer']")) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (document.querySelector("[data-testid='media-viewer']")) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <DialogTitle className="font-display text-2xl uppercase tracking-wider text-white">
            {isEdit ? "Edit Contact" : "New Contact"}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-white/50 flex items-center gap-2 mt-1">
            <MapPin className="h-3 w-3 text-[#f5d300]" />
            <span className="truncate">
              {[form.town || locName?.split(",")[0], form.region]
                .filter(Boolean)
                .join(" · ") ||
                (lat != null ? `${lat.toFixed(5)}, ${lng?.toFixed(5)}` : "Location")}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 overflow-y-auto thin-scroll space-y-5 flex-1">
          {/* Profile picture */}
          <div className="flex items-center gap-4">
            <AvatarUploader
              value={form.avatar}
              onChange={(v) => set("avatar", v)}
              name={form.name}
            />
            <div className="flex-1 min-w-0">
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono">
                Profile picture
              </Label>
              <p className="text-[11px] font-mono text-white/45 mt-1 leading-relaxed">
                Tap the avatar to upload a photo. Resized to ~50&nbsp;KB before saving.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono">
                Name *
              </Label>
              <Input
                ref={nameInputRef}
                value={form.name}
                onChange={(e) => {
                  set("name", e.target.value);
                  if (nameError) setNameError("");
                }}
                placeholder="Contact name"
                className={`bg-transparent border-white/20 neon-input h-12 font-mono ${nameError ? "border-red-500/70 focus:border-red-500" : ""}`}
                data-testid="contact-name-input"
                aria-invalid={!!nameError}
              />
              {nameError && (
                <div
                  className="flex items-center gap-1.5 text-xs text-red-400 font-mono"
                  data-testid="contact-name-error"
                >
                  <AlertCircle className="h-3 w-3" /> {nameError}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono">
                Phone
              </Label>
              <Input
                value={form.phone || ""}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+44 ..."
                className="bg-transparent border-white/20 neon-input h-12 font-mono"
                data-testid="contact-phone-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono">
                Email
              </Label>
              <Input
                value={form.email || ""}
                onChange={(e) => set("email", e.target.value)}
                placeholder="name@example.co.uk"
                className="bg-transparent border-white/20 neon-input h-12 font-mono"
                data-testid="contact-email-input"
              />
            </div>

            <div className="sm:col-span-2 rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-[#f5d300]" />
                  <span className="text-xs uppercase tracking-widest text-white/60 font-mono">
                    Location
                  </span>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[#00e5ff]/80 font-mono">
                  Type to search · pick to move the pin
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* 1) Country (searchable via a datalist so the user can just
                       type and pick) */}
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">
                    Country
                  </Label>
                  <Input
                    list="contact-country-list"
                    value={form.country || ""}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="Start typing or pick…"
                    className="bg-transparent border-white/20 neon-input h-11 font-mono"
                    data-testid="contact-country-input"
                    autoComplete="off"
                  />
                  <datalist id="contact-country-list">
                    {COUNTRY_LIST.map((c) => (
                      <option key={c.code} value={c.name} />
                    ))}
                  </datalist>
                </div>

                {/* 2) Province / State — label + list adapt to country */}
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">
                    {regionLabelForCountry(form.country)}
                  </Label>
                  {(() => {
                    const opts = regionOptionsForCountry(form.country);
                    if (opts) {
                      return (
                        <>
                          <Input
                            list={`contact-region-list-${(form.country || "").replace(/\s+/g, "-")}`}
                            value={form.region || ""}
                            onChange={(e) => set("region", e.target.value)}
                            placeholder={
                              (form.country || "").toLowerCase() === "canada"
                                ? "Ontario"
                                : "Nevada"
                            }
                            className="bg-transparent border-white/20 neon-input h-11 font-mono"
                            data-testid="contact-region-input"
                            autoComplete="off"
                          />
                          <datalist
                            id={`contact-region-list-${(form.country || "").replace(/\s+/g, "-")}`}
                          >
                            {opts.map((o) => (
                              <option key={o} value={o} />
                            ))}
                          </datalist>
                        </>
                      );
                    }
                    return (
                      <Input
                        value={form.region || ""}
                        onChange={(e) => set("region", e.target.value)}
                        placeholder="Greater Manchester"
                        className="bg-transparent border-white/20 neon-input h-11 font-mono"
                        data-testid="contact-region-input"
                      />
                    );
                  })()}
                </div>

                {/* 3) Town / City */}
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">
                    Town / City
                  </Label>
                  <LocationAutocomplete
                    value={form.town || ""}
                    onChange={(v) => set("town", v)}
                    onPick={applyPickedPlace}
                    placeholder="London"
                    dataTestId="contact-town-input"
                    context={{ country: form.country, region: form.region }}
                  />
                </div>

                {/* 4) Postal / ZIP Code */}
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">
                    Postal / ZIP Code
                  </Label>
                  <LocationAutocomplete
                    value={form.postcode || ""}
                    onChange={(v) => set("postcode", v)}
                    onPick={applyPickedPlace}
                    placeholder="M1 1AA · N6A · 89109"
                    dataTestId="contact-postcode-input"
                    uppercase
                    context={{ country: form.country, region: form.region, town: form.town }}
                    minChars={2}
                  />
                </div>

                {/* 5) Address / Street (optional) — full width */}
                <div className="sm:col-span-2 space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest text-white/50 font-mono">
                    Address / Street (optional)
                  </Label>
                  <LocationAutocomplete
                    value={form.street || ""}
                    onChange={(v) => set("street", v)}
                    onPick={applyPickedPlace}
                    placeholder="Type 123 Dundas St · we'll suggest matches"
                    dataTestId="contact-street-input"
                    context={{ country: form.country, region: form.region, town: form.town }}
                  />
                </div>
              </div>
              <div className="text-[10px] font-mono pt-1">
                {lat != null && lng != null ? (
                  <span className="text-white/40">
                    Pinned at {lat?.toFixed(5)}, {lng?.toFixed(5)}
                  </span>
                ) : (
                  <span className="text-[#00e5ff]">
                    Tip: type a postcode (most accurate) — we&apos;ll drop the pin for you on save.
                  </span>
                )}
              </div>
            </div>

            {/* Tags */}
            <div className="sm:col-span-2 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest text-white/60 font-mono">
                  Tags
                </Label>
                {onOpenTagLibrary && (
                  <Button
                    type="button"
                    onClick={onOpenTagLibrary}
                    variant="ghost"
                    size="sm"
                    className="text-[#00e5ff] hover:text-[#00e5ff] hover:bg-[#00e5ff]/10 font-mono uppercase tracking-wider text-xs"
                    data-testid="contact-open-tag-library"
                  >
                    <Library className="h-3.5 w-3.5 mr-1" /> Manage tags
                  </Button>
                )}
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
                <TagPicker
                  folders={folders}
                  tags={tags}
                  selectedTagIds={form.tag_ids}
                  onChange={(ids) => set("tag_ids", ids)}
                  onCreateFolder={onOpenTagLibrary}
                  emptyMessage="No folders or tags yet. Open the Tag Library to set up Gender, Age, Attributes, etc."
                />
              </div>
            </div>

            {/* Notes */}
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono">
                Notes
              </Label>
              <Textarea
                value={form.notes || ""}
                onChange={(e) => set("notes", e.target.value)}
                placeholder="Anything to remember…"
                rows={3}
                className="bg-transparent border-white/20 neon-input font-mono resize-none"
                data-testid="contact-notes-input"
              />
            </div>

            {/* Media gallery */}
            <div className="sm:col-span-2 space-y-2 pt-2 border-t border-white/10">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest text-white/60 font-mono inline-flex items-center gap-2">
                  <Camera className="h-3.5 w-3.5" /> Media Gallery
                </Label>
                <span className="text-[10px] font-mono text-white/40">
                  {(form.media || []).length} item{(form.media || []).length === 1 ? "" : "s"}
                </span>
              </div>
              <MediaGallery
                media={form.media || []}
                onChange={(m) => set("media", m)}
              />
            </div>
          </div>

          {/* Custom fields — labels + values, links become clickable on the profile */}
          <div className="space-y-2 pt-2 border-t border-white/10">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono inline-flex items-center gap-2">
                <LinkIcon className="h-3.5 w-3.5" /> Links & Details
              </Label>
              <Button
                type="button"
                onClick={addCustomField}
                size="sm"
                variant="ghost"
                className="text-[#00e5ff] hover:text-[#00e5ff] hover:bg-[#00e5ff]/10 font-mono uppercase tracking-wider text-xs"
                data-testid="contact-add-field-button"
              >
                <Plus className="h-4 w-4 mr-1" /> Add row
              </Button>
            </div>
            <div className="space-y-2">
              {form.custom_fields.length === 0 && (
                <p className="text-xs text-white/40 font-mono">
                  Add any title + value pairs (e.g. &quot;Website&quot;, &quot;Hotel they love&quot;,
                  &quot;Instagram&quot;). Paste full URLs and they&apos;ll become tappable links on
                  the profile.
                </p>
              )}
              {form.custom_fields.map((cf, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={cf.key}
                    onChange={(e) => updateCustomField(idx, "key", e.target.value)}
                    placeholder="Title (e.g. Website)"
                    className="bg-transparent border-white/20 neon-input h-11 font-mono w-1/3"
                    data-testid={`custom-field-key-${idx}`}
                  />
                  <Input
                    value={cf.value}
                    onChange={(e) => updateCustomField(idx, "value", e.target.value)}
                    placeholder="Value or https://…"
                    className="bg-transparent border-white/20 neon-input h-11 font-mono flex-1"
                    data-testid={`custom-field-value-${idx}`}
                  />
                  <Button
                    type="button"
                    onClick={() => removeCustomField(idx)}
                    size="icon"
                    variant="ghost"
                    className="h-11 w-11 text-white/50 hover:text-[#ff2a2a] hover:bg-[#ff2a2a]/10"
                    data-testid={`custom-field-remove-${idx}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-white/10 flex flex-row items-center !justify-between gap-3">
          <div>
            {isEdit && (
              <Button
                onClick={() => onDelete?.(initialContact)}
                variant="ghost"
                className="text-[#ff2a2a] hover:bg-[#ff2a2a]/10 hover:text-[#ff2a2a] font-mono uppercase tracking-wider"
                data-testid="contact-delete-button"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => onOpenChange(false)}
              variant="ghost"
              className="btn-ghost h-11 px-5"
              data-testid="contact-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="btn-neon h-11 px-6"
              disabled={saving}
              data-testid="contact-save-button"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
                </>
              ) : (
                <>Save Contact</>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AvatarUploader({ value, onChange, name }) {  const ref = useRef(null);
  const initials = (name || "")
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const pick = () => ref.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const data = await resizeImageToDataURL(f, { maxEdge: 480, quality: 0.85 });
      onChange(data);
    } catch {
      toast.error("Couldn't process image");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="relative" data-testid="contact-avatar-uploader">
      <button
        type="button"
        onClick={pick}
        className="h-20 w-20 rounded-full overflow-hidden border-2 border-[#00e5ff]/40 hover:border-[#00e5ff] focus:border-[#00e5ff] focus:outline-none transition-colors flex items-center justify-center bg-white/[0.04] relative group"
        aria-label="Upload profile picture"
        data-testid="contact-avatar-button"
      >
        {value ? (
          <img src={value} alt="" className="w-full h-full object-cover" draggable={false} />
        ) : initials ? (
          <span className="font-display text-2xl tracking-wider text-[#00e5ff]">{initials}</span>
        ) : (
          <User2 className="h-7 w-7 text-[#00e5ff]/70" />
        )}
        <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <Camera className="h-5 w-5 text-white" />
        </span>
      </button>
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-[#111] border border-white/20 text-white/70 hover:text-[#ff5a5a] flex items-center justify-center"
          aria-label="Remove avatar"
          data-testid="contact-avatar-remove"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
        data-testid="contact-avatar-input"
      />
    </div>
  );
}


function LocationAutocomplete({
  value,
  onChange,
  onPick,
  placeholder,
  dataTestId,
  uppercase = false,
  context,
  minChars = 3,
  debounceMs = 350,
}) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const lastPickedRef = useRef("");
  const timer = useRef(null);
  const wrapRef = useRef(null);
  // Snapshot context so useEffect doesn't retrigger on unrelated re-renders
  const ctxKey = [context?.country, context?.region, context?.town]
    .map((s) => (s || "").toLowerCase())
    .join("|");

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    // Don't research immediately after a pick (we just set the field).
    if (value && value === lastPickedRef.current) {
      setResults([]);
      setOpen(false);
      return;
    }
    if (!value || value.trim().length < minChars || !focused) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const data = context
          ? await searchPlacesScoped(value, context)
          : await searchPlaces(value);
        setResults(data);
        setOpen(data.length > 0);
      } finally {
        setLoading(false);
      }
    }, debounceMs);
    return () => timer.current && clearTimeout(timer.current);
  }, [value, focused, ctxKey, minChars, debounceMs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("touchstart", onClickOutside);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("touchstart", onClickOutside);
    };
  }, []);

  const handlePick = (r) => {
    // Remember the value we set so the next useEffect doesn't re-search.
    const picked = [r.street, r.town, r.postcode].filter(Boolean)[0] || r.short || "";
    lastPickedRef.current = picked;
    onPick?.(r);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={`${dataTestId}-autocomplete`}>
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            setFocused(true);
            if (results.length) setOpen(true);
          }}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          className={`bg-transparent border-white/20 neon-input h-11 font-mono pr-9 ${
            uppercase ? "uppercase" : ""
          }`}
          data-testid={dataTestId}
        />
        {loading && (
          <Loader2 className="h-4 w-4 text-[#00e5ff] animate-spin absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        )}
      </div>
      {open && results.length > 0 && (
        <div
          className="absolute z-[60] left-0 right-0 mt-1 rounded-xl border border-white/10 bg-[#0a0a0a]/97 backdrop-blur-xl shadow-2xl overflow-hidden"
          data-testid={`${dataTestId}-results`}
        >
          <ul className="max-h-64 overflow-auto thin-scroll">
            {results.map((r, idx) => (
              <li key={r.id}>
                <button
                  type="button"
                  // Use onMouseDown so the click fires before the input blurs
                  // and closes the dropdown.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handlePick(r);
                  }}
                  className="w-full text-left px-3 py-2 flex items-start gap-2 transition-colors text-white/80 hover:bg-[#00e5ff]/10 hover:text-[#00e5ff]"
                  data-testid={`${dataTestId}-result-${idx}`}
                >
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[#f5d300]" />
                  <div className="min-w-0">
                    <div className="text-sm font-display uppercase tracking-wider truncate">
                      {r.short}
                    </div>
                    <div className="text-[11px] text-white/50 truncate font-mono">{r.name}</div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
